/**
 * Product Detail Page
 *
 * Shows full information for a single menu item.
 * URL: /product/:id
 *
 * Reached by clicking any product card on the Home page (featured section,
 * popular treats section, etc.). Deep-linked from menuPathForMenuItemId().
 *
 * Features:
 * - Fetches item data from GET /api/menu/:id
 * - Large hero image with fallback placeholder
 * - Category badge, gluten-free badge, price
 * - Add to Cart with quantity controls
 * - Handles special items: Stuffed Cookies → StuffedCookieSelectionModal
 *                          Butter Tarts   → ButterTartSelectionModal
 *                          Sandwiches/Drinks/Soups → ProductCustomization modal
 * - "Back to Menu" link and "View Full Menu" CTA
 */

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeftIcon,
  ShoppingCartIcon,
  PlusIcon,
  MinusIcon,
  CheckIcon,
  XMarkIcon,
  MagnifyingGlassPlusIcon,
} from '@heroicons/react/24/outline';
import { useCart } from '../context/CartContext';
import ProductCustomization, { ProductCustomizations } from '../components/ProductCustomization';
import StuffedCookieSelectionModal from '../components/StuffedCookieSelectionModal';
import ButterTartSelectionModal from '../components/ButterTartSelectionModal';
import ShareButton from '../components/ShareButton';
import { useSiteImageOverrides } from '../hooks/useSiteImageOverrides';
import { menuItemElementId } from '../utils/menuDeepLink';
import { publicLoadErrorMessage } from '../lib/publicSiteErrorMessage';
import { applySeo } from '../utils/seo';
import { BreadcrumbSchema, ProductSchema } from '../components/StructuredData';
import {
  normalizeDetailFit,
  productDetailImageClass,
  productDetailImageFrameClass,
} from '../utils/productDetailImage';

const API_URL = import.meta.env.VITE_API_URL || '';

// Full menu item type returned by GET /api/menu/:id
interface MenuItem {
  id: string;
  name: string;
  description: string | null;
  price: number;
  category: string;
  image_url: string | null;
  image_object_position?: string | null;
  /** contain = original file on this page; cover = fill square like menu cards */
  image_detail_fit?: string | null;
  is_available: boolean;
  is_featured: boolean;
  is_gluten_free?: boolean;
  /** Tammy marks this true in admin each morning — shows a green "Baking Today" badge */
  is_baking_today?: boolean;
  tax_applies?: boolean | null;
}

// ─── Category icons for the badge ────────────────────────────────────────────
const CATEGORY_ICONS: Record<string, string> = {
  'Custom Cakes': '🎂',
  'Cheesecakes': '🍰',
  'Stuffed Cookies': '🍪',
  'Cookies': '🍪',
  'Cookie Sandwiches': '🍪',
  'Tarts': '🥧',
  'Bars, Squares & Treats': '🍫',
  'Sandwiches & Savory': '🥖',
  'Gluten Free': '🌾',
  'Pastries': '🥐',
  'Muffins': '🧁',
  'Valentines': '💝',
  'Seasonal': '🍂',
  'Soups': '🍲',
  'Drinks': '☕',
};

// ─── Helpers (mirror Menu.tsx logic) ─────────────────────────────────────────

/** Items that open the ProductCustomization modal (toppings/bread/size/milk) */
const isCustomizable = (item: MenuItem) =>
  item.category === 'Sandwiches & Savory' ||
  item.category === 'Drinks' ||
  item.category === 'Soups';

/** Butter tarts need a Plain/Pecan choice */
const isButterTart = (item: MenuItem) =>
  item.category === 'Tarts' && item.name.toLowerCase().includes('butter tart');

// ─── Component ────────────────────────────────────────────────────────────────

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getImage } = useSiteImageOverrides();
  const { addItem, items: cartItems, updateQuantity, totalItems } = useCart();

  // ── Fetch state ──
  const [item, setItem] = useState<MenuItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Local quantity for non-customizable items ──
  const [qty, setQty] = useState(1);

  // ── "Added" flash feedback ──
  const [justAdded, setJustAdded] = useState(false);

  // ── Image lightbox (fullscreen zoom on click) ──
  const [imageZoomed, setImageZoomed] = useState(false);

  // ── Related items from the same category ──
  const [relatedItems, setRelatedItems] = useState<MenuItem[]>([]);

  // ── Modal state (same pattern as Menu.tsx) ──
  const [customizationItem, setCustomizationItem] = useState<MenuItem | null>(null);
  const [stuffedCookieItem, setStuffedCookieItem] = useState<MenuItem | null>(null);
  const [butterTartItem, setButterTartItem] = useState<MenuItem | null>(null);

  // ── Load item from API ──
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);

    fetch(`${API_URL}/api/menu/${encodeURIComponent(id)}`)
      .then((res) => {
        if (res.status === 404) throw new Error('not_found');
        if (!res.ok) throw new Error('load_error');
        return res.json();
      })
      .then((data: MenuItem) => {
        setItem(data);
        // Set page title, description, and OG tags so shares/previews show the product name
        applySeo({
          title: `${data.name} | Nanny B's Bakery`,
          description: data.description
            ? `${data.description.slice(0, 155).trim()}…`
            : `${data.name} — fresh from Nanny B's Bakery in Barrie, Ontario. $${data.price.toFixed(2)} CAD.`,
          canonicalPath: `/product/${data.id}`,
          og: {
            type: 'website',
            title: `${data.name} | Nanny B's Bakery`,
            image: data.image_url ?? undefined,
          },
        });
      })
      .catch((err) => {
        console.error('ProductDetail fetch error:', err);
        setError(err.message === 'not_found' ? 'not_found' : 'load_error');
      })
      .finally(() => setLoading(false));
  }, [id]);

  // ── Fetch related items from the same category (after main item loads) ──
  useEffect(() => {
    if (!item) return;
    fetch(`${API_URL}/api/menu?category=${encodeURIComponent(item.category)}`)
      .then((r) => r.ok ? r.json() : { items: [] })
      .then((data: { items?: MenuItem[] } | MenuItem[]) => {
        // API returns { items }; tolerate a bare array if a test stub sends one
        const list = Array.isArray(data) ? data : (data.items || []);
        const others = list.filter((i) => i.id !== item.id && i.is_available);
        const shuffled = others.sort(() => Math.random() - 0.5).slice(0, 3);
        setRelatedItems(shuffled);
      })
      .catch(() => {}); // fail silently — related items are bonus, not critical
  }, [item?.id]);

  // ── How many of this item are already in the cart ──
  const cartQty = cartItems
    .filter((ci) => ci.id === item?.id && !ci.customizations)
    .reduce((sum, ci) => sum + ci.quantity, 0);

  // ── Add to cart for simple items ──
  const handleAddSimple = () => {
    if (!item) return;
    addItem(
      {
        id: item.id,
        name: item.name,
        price: item.price,
        image: item.image_url || getImage('placeholder_menu', '/assets/images/placeholder.webp'),
        category: item.category,
        tax_applies: item.tax_applies ?? undefined,
      },
      qty,
    );
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 2000);
  };

  // ── Route the "Add to Cart" click to the right handler ──
  const handleAddToCart = () => {
    if (!item) return;
    if (isCustomizable(item)) {
      setCustomizationItem(item);
      return;
    }
    if (item.category === 'Stuffed Cookies') {
      setStuffedCookieItem(item);
      return;
    }
    if (isButterTart(item)) {
      setButterTartItem(item);
      return;
    }
    handleAddSimple();
  };

  // ── ProductCustomization confirm ──
  const handleCustomization = (customizations: ProductCustomizations, extraCost: number) => {
    if (!customizationItem) return;
    const noteParts: string[] = [];
    Object.values(customizations).forEach((group) => {
      if (group.selections.length > 0) {
        const names = group.selections.map((s) => s.name).join(', ');
        noteParts.push(`${group.groupName}: ${names}`);
      }
    });
    addItem({
      id: customizationItem.id,
      name: customizationItem.name,
      price: customizationItem.price + extraCost,
      image: customizationItem.image_url || getImage('placeholder_menu', '/assets/images/placeholder.webp'),
      notes: noteParts.join(' | '),
      customizations,
      category: customizationItem.category,
      tax_applies: customizationItem.tax_applies ?? undefined,
    });
    setCustomizationItem(null);
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 2000);
  };

  // ── StuffedCookie confirm ──
  const handleStuffedCookieConfirm = (selection: string) => {
    if (!stuffedCookieItem) return;
    addItem({
      id: stuffedCookieItem.id,
      name: stuffedCookieItem.name,
      price: stuffedCookieItem.price,
      image: stuffedCookieItem.image_url || getImage('placeholder_menu', '/assets/images/placeholder.webp'),
      notes: selection,
      category: stuffedCookieItem.category,
      tax_applies: stuffedCookieItem.tax_applies ?? undefined,
    });
    setStuffedCookieItem(null);
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 2000);
  };

  // ── ButterTart confirm ──
  const handleButterTartConfirm = (selection: string) => {
    if (!butterTartItem) return;
    addItem({
      id: butterTartItem.id,
      name: butterTartItem.name,
      price: butterTartItem.price,
      image: butterTartItem.image_url || getImage('placeholder_menu', '/assets/images/placeholder.webp'),
      notes: selection,
      category: butterTartItem.category,
      tax_applies: butterTartItem.tax_applies ?? undefined,
    });
    setButterTartItem(null);
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 2000);
  };

  // ── Update quantity for a simple (non-customized) cart line ──
  const handleUpdateCartQty = (newQty: number) => {
    if (!item) return;
    const cartLine = cartItems.find(
      (ci) => ci.id === item.id && !ci.customizations,
    );
    if (cartLine) {
      updateQuantity(cartLine.lineId, Math.max(0, newQty));
    }
  };

  // ─── LOADING ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-bakery-cream to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600">Loading product…</p>
        </div>
      </div>
    );
  }

  // ─── ERROR / NOT FOUND ──────────────────────────────────────────────────────
  if (error || !item) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-bakery-cream to-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">🍰</div>
          <h1 className="text-2xl font-display font-bold text-gray-900 mb-2">
            {error === 'not_found' ? 'Product Not Found' : 'Oops!'}
          </h1>
          <p className="text-gray-600 mb-6">
            {error === 'not_found'
              ? 'This item is no longer available. Browse our full menu below!'
              : publicLoadErrorMessage()}
          </p>
          <Link to="/menu" className="btn-primary">
            View Full Menu
          </Link>
        </div>
      </div>
    );
  }

  const icon = CATEGORY_ICONS[item.category] ?? '🧁';
  const objectPosition = item.image_object_position ?? '50% 50%';
  const detailFit = normalizeDetailFit(item.image_detail_fit);

  // ─── DETAIL VIEW ────────────────────────────────────────────────────────────
  return (
    <div className="bg-gradient-to-b from-bakery-cream to-white min-h-screen">
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: 'https://nannybbakery.ca/' },
          { name: 'Menu', url: 'https://nannybbakery.ca/menu' },
          { name: item.name, url: `https://nannybbakery.ca/product/${item.id}` },
        ]}
      />
      <ProductSchema
        id={item.id}
        name={item.name}
        description={item.description || undefined}
        price={item.price}
        image={item.image_url || undefined}
        isAvailable={item.is_available}
        category={item.category}
      />
      {/* ── Floating Cart Button (same as Menu.tsx) ── */}
      {totalItems > 0 && (
        <Link
          to="/order"
          className="fixed bottom-6 right-6 z-40 bg-primary-600 text-white p-4 rounded-full shadow-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
        >
          <ShoppingCartIcon className="h-6 w-6" />
          <span className="font-bold">{totalItems}</span>
        </Link>
      )}

      {/* ── Back Navigation ── */}
      <div className="container-custom pt-6 pb-2">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back
        </button>
      </div>

      {/* ── Main Content ── */}
      <section className="section-padding pt-4">
        <div className="container-custom">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden max-w-4xl mx-auto">
            <div className="md:flex">

              {/* ── Image (click to zoom fullscreen) ── */}
              <div
                className={`${productDetailImageFrameClass(detailFit)} ${item.image_url ? 'cursor-zoom-in' : ''}`}
                onClick={() => item.image_url && setImageZoomed(true)}
              >
                {item.image_url ? (
                  <>
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className={productDetailImageClass(detailFit)}
                      style={detailFit === 'cover' ? { objectPosition } : undefined}
                    />
                    {/* Zoom hint icon in corner */}
                    <div className="absolute top-2 right-2 bg-black/40 text-white rounded-full p-1.5 opacity-70 hover:opacity-100 transition-opacity">
                      <MagnifyingGlassPlusIcon className="w-4 h-4" />
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-8xl">
                    {icon}
                  </div>
                )}

                {/* Unavailable overlay */}
                {!item.is_available && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="bg-white text-gray-800 font-bold px-4 py-2 rounded-full text-sm shadow">
                      Currently Unavailable
                    </span>
                  </div>
                )}
              </div>

              {/* ── Info Panel ── */}
              <div className="md:w-1/2 p-6 md:p-8 flex flex-col justify-between">
                <div>
                  {/* Category + badges */}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="inline-flex items-center gap-1 text-sm bg-primary-50 text-primary-700 px-3 py-1 rounded-full font-medium">
                      {icon} {item.category}
                    </span>
                    {item.is_gluten_free && (
                      <span className="inline-flex items-center gap-1 text-sm bg-green-50 text-green-700 px-3 py-1 rounded-full font-medium">
                        🌾 Gluten Free
                      </span>
                    )}
                    {item.is_baking_today && (
                      <span className="inline-flex items-center gap-1 text-sm bg-green-50 text-green-700 px-3 py-1 rounded-full font-bold animate-pulse">
                        🟢 Baking Today!
                      </span>
                    )}
                    {item.is_featured && (
                      <span className="inline-flex items-center gap-1 text-sm bg-amber-50 text-amber-700 px-3 py-1 rounded-full font-medium">
                        ⭐ Featured
                      </span>
                    )}
                  </div>

                  {/* Name */}
                  <motion.h1
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-3"
                  >
                    {item.name}
                  </motion.h1>

                  {/* Price + Share button on the same row */}
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-2xl font-bold text-primary-600">
                      ${item.price.toFixed(2)}{' '}
                      <span className="text-sm font-normal text-gray-400">CAD</span>
                    </p>
                    <ShareButton
                      title={item.name}
                      text={`Check out ${item.name} from Nanny B's Bakery! 🧁`}
                      url={`https://nannybbakery.ca/product/${encodeURIComponent(item.id)}`}
                      imageUrl={item.image_url ?? undefined}
                      size="sm"
                    />
                  </div>

                  {/* Description */}
                  {item.description && (
                    <p className="text-gray-600 leading-relaxed mb-6">
                      {item.description}
                    </p>
                  )}

                  {/* Special category notes */}
                  {item.category === 'Custom Cakes' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800">
                      ⏰ Please give us <strong>72 hours notice</strong> on any cake order. Thank you!
                    </div>
                  )}
                  {item.category === 'Stuffed Cookies' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-800">
                      🍪 You'll pick your flavours when you add to cart.
                    </div>
                  )}
                  {item.category === 'Tarts' && isButterTart(item) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-800">
                      🥧 You'll choose Plain or Pecan when you add to cart.
                    </div>
                  )}
                  {isCustomizable(item) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-800">
                      ✏️ You'll customize your order when you add to cart.
                    </div>
                  )}
                </div>

                {/* ── Add to Cart / Quantity ── */}
                <div className="mt-6 space-y-3">
                  {item.is_available ? (
                    <>
                      {/* Quantity selector — only for simple (non-customizable) items */}
                      {!isCustomizable(item) &&
                        item.category !== 'Stuffed Cookies' &&
                        !isButterTart(item) && (
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-600 font-medium">Qty:</span>
                            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                              <button
                                onClick={() => setQty((q) => Math.max(1, q - 1))}
                                className="px-3 py-2 hover:bg-gray-100 transition-colors"
                                aria-label="Decrease quantity"
                              >
                                <MinusIcon className="w-4 h-4" />
                              </button>
                              <span className="px-4 py-2 font-semibold min-w-[2.5rem] text-center">
                                {qty}
                              </span>
                              <button
                                onClick={() => setQty((q) => q + 1)}
                                className="px-3 py-2 hover:bg-gray-100 transition-colors"
                                aria-label="Increase quantity"
                              >
                                <PlusIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )}

                      {/* Add to cart button */}
                      <button
                        onClick={handleAddToCart}
                        className={`w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-semibold text-lg transition-all duration-200 ${
                          justAdded
                            ? 'bg-green-500 text-white'
                            : 'btn-primary'
                        }`}
                      >
                        {justAdded ? (
                          <>
                            <CheckIcon className="w-5 h-5" />
                            Added to Cart!
                          </>
                        ) : (
                          <>
                            <ShoppingCartIcon className="w-5 h-5" />
                            Add to Cart
                          </>
                        )}
                      </button>

                      {/* If already in cart, show cart quantity + quick update */}
                      {cartQty > 0 && !isCustomizable(item) && (
                        <div className="flex items-center justify-between bg-primary-50 rounded-lg px-4 py-2 text-sm">
                          <span className="text-primary-700 font-medium">
                            {cartQty} in cart
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleUpdateCartQty(cartQty - 1)}
                              className="text-primary-600 hover:text-primary-800"
                              aria-label="Remove one from cart"
                            >
                              <MinusIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleUpdateCartQty(cartQty + 1)}
                              className="text-primary-600 hover:text-primary-800"
                              aria-label="Add one to cart"
                            >
                              <PlusIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Go to cart / checkout */}
                      {totalItems > 0 && (
                        <Link
                          to="/order"
                          className="w-full block text-center btn-outline py-2.5"
                        >
                          View Cart & Checkout
                        </Link>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-gray-500 mb-4">
                        This item is currently unavailable.
                      </p>
                      <Link to="/menu" className="btn-outline">
                        Browse Other Items
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── You Might Also Like ── */}
      {relatedItems.length > 0 && (
        <section className="section-padding pt-0">
          <div className="container-custom max-w-4xl mx-auto">
            <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">
              You might also like
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedItems.map((related, i) => (
                <motion.div
                  key={related.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                >
                  <Link
                    to={`/product/${related.id}`}
                    className="block bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow overflow-hidden group"
                  >
                    {/* Image */}
                    <div className="aspect-square overflow-hidden bg-gray-100">
                      {related.image_url ? (
                        <img
                          src={related.image_url}
                          alt={related.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-5xl">
                          {CATEGORY_ICONS[related.category] ?? '🧁'}
                        </div>
                      )}
                    </div>
                    {/* Info */}
                    <div className="p-3">
                      <p className="font-display font-semibold text-gray-900 text-sm leading-tight mb-1 line-clamp-2">
                        {related.name}
                      </p>
                      <p className="text-primary-600 font-bold text-sm">
                        ${related.price.toFixed(2)}
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Back to menu CTA ── */}
      <section className="py-10 text-center">
        <p className="text-gray-600 mb-4">Want to explore more?</p>
        <Link to="/menu" className="btn-primary">
          View Full Menu
        </Link>
      </section>

      {/* ── Fullscreen image lightbox ── */}
      {imageZoomed && item.image_url && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setImageZoomed(false)}
        >
          {/* Close button */}
          <button
            className="absolute top-4 right-4 text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"
            onClick={() => setImageZoomed(false)}
            aria-label="Close image"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
          {/* Full image — constrained to viewport */}
          <img
            src={item.image_url}
            alt={item.name}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <p className="absolute bottom-4 left-0 right-0 text-center text-white/70 text-sm">
            {item.name} — tap anywhere to close
          </p>
        </div>
      )}

      {/* ── Modals (same as Menu.tsx) ── */}

      {/* Sandwiches / Drinks / Soups customization */}
      {customizationItem && (
        <ProductCustomization
          isOpen={!!customizationItem}
          onClose={() => setCustomizationItem(null)}
          onConfirm={handleCustomization}
          itemName={customizationItem.name}
          itemPrice={customizationItem.price}
          category={customizationItem.category}
          menuItemId={customizationItem.id}
        />
      )}

      {/* Stuffed Cookie flavour picker */}
      {stuffedCookieItem && (
        <StuffedCookieSelectionModal
          isOpen={!!stuffedCookieItem}
          onClose={() => setStuffedCookieItem(null)}
          onConfirm={handleStuffedCookieConfirm}
          productId={stuffedCookieItem.id}
          productName={stuffedCookieItem.name}
          productPrice={stuffedCookieItem.price}
        />
      )}

      {/* Butter Tart flavour picker */}
      {butterTartItem && (
        <ButterTartSelectionModal
          isOpen={!!butterTartItem}
          onClose={() => setButterTartItem(null)}
          onConfirm={handleButterTartConfirm}
          productId={butterTartItem.id}
          productName={butterTartItem.name}
          productPrice={butterTartItem.price}
        />
      )}

      {/* Hidden anchor for deep-link scrolling (used by menuItemElementId utility) */}
      <div id={item ? menuItemElementId(item.id) : undefined} />
    </div>
  );
}
